import { Component, computed, inject, Signal, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { TableModule } from "@openng/optimus-ui/table";
import { Column } from "../../../../models/ui/tableColumn";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DeviceManagementService } from "../../../../services/api/device-management/device-management.service";
import { toSignal } from "@angular/core/rxjs-interop";
import { SelectModule } from "@openng/optimus-ui/select";
import { FormsModule } from "@angular/forms";
import { Software } from "../../../../models/api/deviceManagement/software";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { Device } from "../../../../models/api/deviceManagement/device";
import { MessageService } from "@openng/optimus-ui/api";

@Component({
  selector: "app-start-update.component",
  imports: [TableModule, TranslateModule, SelectModule, FormsModule, ButtonComponent],
  templateUrl: "./start-update.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./start-update.component.scss",
})
export class StartUpdateComponent {
  private config = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);
  private deviceManagement = inject(DeviceManagementService);
  private message = inject(MessageService);
  private translate = inject(TranslateService);

  public deviceType = signal(this.config.data.deviceType);
  public devices = signal(this.config.data.devices);

  public softwareVersions: Signal<Software[]> = toSignal(this.deviceManagement.getSoftwares(this.deviceType()), {
    initialValue: [],
  });
  public selectedVersion: WritableSignal<string | null> = signal(null);

  public devicesColumns: Column[] = [{ field: "coreId", header: "device_management.devices.table.core_id" }];

  public fieldsValid = computed(() => {
    return this.selectedVersion() !== null;
  });

  /**
   * Closes the dialog without returning any value.
   */
  public closeDialog() {
    this.ref.close();
  }

  /**
   * Starts the software update process for the selected devices.
   * If the update is successful, a success message is displayed and the dialog is closed with a success flag.
   */
  public save() {
    const deviceIds: string[] = this.devices().map((device: Device) => device.coreId);
    const version = this.selectedVersion();
    const deviceType = this.deviceType();
    if (this.fieldsValid() && version) {
      this.deviceManagement.createUpdate(version, deviceType, deviceIds).subscribe({
        next: () => {
          this.message.add({
            severity: "success",
            key: "global",
            life: 4500,
            summary: this.translate.instant("common.success"),
            detail: this.translate.instant("device_management.devices.update_dialog.success"),
          });
          this.ref.close(true);
        },
      });
    }
  }
}
